import React, { useState, useCallback, useEffect, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ActionButton } from 'components/modals';
import { EnterpriseScreenShell, actionButtonFactory } from 'components/enterprise-shell';
import { SyutGrid, type SyutGridColumn } from 'components/ui';
import notificationService from 'services/notificationService';
import { reportService } from 'api/services';
import type { DayBookEntryDto, DayBookResultDto } from 'api/types';
import { useHasPermission } from 'hooks/usePermissions';
import ReportHeader from 'components/reports/ReportHeader';
import DrillableCell from 'components/reports/DrillableCell';

const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const money = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (s?: string) => (s ? new Date(s).toLocaleDateString() : '');

const inp: React.CSSProperties = { fontSize: '0.8125rem', padding: '0.375rem 0.625rem' };

interface DayBookScreenProps {
  moduleIcon?: string;
  tabId?: string;
}

// Grid row is a DayBookEntryDto augmented with an `id` (SyutGrid requires it) and
// an `isGroupStart` flag telling us this row is the first line of its voucher.
// Cell renderers use isGroupStart to blank Date/Voucher/Type on continuation lines,
// giving the "one block per voucher" visual grouping without leaving SyutGrid.
interface DayBookGridRow extends DayBookEntryDto {
  id: string;
  isGroupStart: boolean;
}

const DayBookScreen: React.FC<DayBookScreenProps> = ({ moduleIcon, tabId }) => {
  const canExport = useHasPermission('day-book', 'EXPORT');
  const [filterOpen, setFilterOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [fromDate, setFromDate] = useState(monthStart());
  const [toDate, setToDate] = useState(today());
  const [result, setResult] = useState<DayBookResultDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 50;

  // Rollup is computed server-side across the FULL matching set (not just this page).
  const totals = { dr: result?.totalDebits ?? 0, cr: result?.totalCredits ?? 0 };

  // Preprocess: attach `id` + `isGroupStart` per row. First occurrence of each transactionId
  // gets isGroupStart=true; subsequent lines get false so we blank the header cells.
  const gridRows: DayBookGridRow[] = useMemo(() => {
    if (!result) return [];
    return result.items.map((r, i, arr) => ({
      ...r,
      id: `${r.transactionId}-${r.lineNo}`,
      isGroupStart: i === 0 || arr[i - 1].transactionId !== r.transactionId,
    }));
  }, [result]);

  const load = useCallback(async (targetPage: number) => {
    setLoading(true);
    try {
      setResult(await reportService.getDayBook(fromDate, toDate, targetPage, pageSize));
      setPage(targetPage);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not load the day book.');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  const run = useCallback(() => load(1), [load]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { run(); }, []);

  const clear = useCallback(() => { setFromDate(monthStart()); setToDate(today()); }, []);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      await reportService.exportDayBook(fromDate, toDate);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not export the day book.');
    } finally {
      setExporting(false);
    }
  }, [fromDate, toDate]);

  // Export button appears only when the user has UI.DAY-BOOK.EXPORT permission.
  const actionButtons: ActionButton[] = [
    actionButtonFactory.refresh(run),
    ...(canExport ? [{ ...actionButtonFactory.export_(handleExport), disabled: exporting }] : []),
  ];

  // ------------------------------------------------------------------
  // SyutGrid column definitions.
  //
  // Grouping trick: cells that describe the VOUCHER (Date, Voucher No, Type)
  // render blank on continuation lines via `row.original.isGroupStart`. The
  // voucher-block visual grouping falls out for free.
  //
  // Drill-down TODO: wrap Voucher No + Ledger cells in DrillableCell with
  // onClick handlers when navigation is wired.
  // ------------------------------------------------------------------
  const columns: SyutGridColumn<DayBookGridRow>[] = useMemo(() => [
    {
      id: 'date',
      header: 'Date',
      size: 100,
      cell: ({ row }) => row.original.isGroupStart ? fmtDate(row.original.transactionDate) : '',
    },
    {
      id: 'voucherNo',
      header: 'Voucher',
      size: 130,
      cell: ({ row }) => row.original.isGroupStart
        ? <DrillableCell>{row.original.transactionNo}</DrillableCell>
        : '',
    },
    {
      id: 'voucherType',
      header: 'Type',
      size: 130,
      cell: ({ row }) => row.original.isGroupStart
        ? (row.original.voucherTypeName || row.original.voucherClass)
        : '',
    },
    {
      id: 'ledger',
      header: 'Ledger',
      cell: ({ row }) => {
        const l = row.original;
        const label = l.ledgerCode ? `${l.ledgerCode} - ${l.ledgerName ?? ''}` : (l.ledgerName ?? '—');
        return <DrillableCell>{label}</DrillableCell>;
      },
    },
    {
      id: 'debit',
      header: 'Debit',
      align: 'right',
      size: 110,
      cell: ({ row }) => row.original.drAmount ? money(row.original.drAmount) : '',
    },
    {
      id: 'credit',
      header: 'Credit',
      align: 'right',
      size: 110,
      cell: ({ row }) => row.original.crAmount ? money(row.original.crAmount) : '',
    },
    {
      id: 'narration',
      header: 'Narration',
      cell: ({ row }) => row.original.lineNarration || row.original.voucherNarration || '',
    },
  ], []);

  return (
    <EnterpriseScreenShell
      title="Day Book"
      description="All vouchers and their postings for a date range"
      icon={moduleIcon && (IconSystem as any)[moduleIcon]
        ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
        : <IconSystem.list size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen }}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>From</label>
                <input type="date" className="form-input" value={fromDate}
                  onChange={e => setFromDate(e.target.value)} style={inp} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>To</label>
                <input type="date" className="form-input" value={toDate}
                  onChange={e => setToDate(e.target.value)} style={inp} />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clear}>Clear</button>
              <button className="btn btn-primary" onClick={run} disabled={loading}>
                {loading ? 'Loading…' : 'Apply'}
              </button>
            </div>
          </div>
        )
      }
    >
      <ReportHeader
        filters={[
          { label: 'Period', value: `${fmtDate(fromDate)} — ${fmtDate(toDate)}` },
        ]}
      />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, border: '1px solid var(--border-color)', borderTop: 'none', borderRadius: '0 0 0.5rem 0.5rem', overflow: 'hidden' }}>
        {/* Flex column, not a block: SyutGrid sizes itself with flex: 1 and scrolls inside its
            own wrapper, so a block parent left it at content height and overflow: hidden then
            clipped every row (and the pager) below the fold — postings vanished from view while
            the totals band still counted them. */}
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <SyutGrid<DayBookGridRow>
            data={gridRows}
            columns={columns}
            isLoading={loading}
            emptyMessage="No postings in this date range"
            enablePagination
            manualPagination
            totalCount={result?.totalCount ?? 0}
            currentPage={page - 1}
            pageSize={pageSize}
            onPageChange={(idx) => load(idx + 1)}
          />
        </div>
        {/* Totals band — sits directly under the grid. Server-side rollup so it
            reflects the FULL matching set, not just the current page. */}
        {result && result.items.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) 110px 110px minmax(0, 1fr)',
            gap: '0.5rem',
            padding: '0.6rem 0.75rem',
            background: 'var(--bg-secondary)',
            borderTop: '1px solid var(--border-color)',
            fontSize: '0.8125rem',
            fontWeight: 700,
            alignItems: 'center',
          }}>
            <span style={{ color: 'var(--text-secondary)' }}>Total (all matching rows)</span>
            <span style={{ textAlign: 'right' }}>{money(totals.dr)}</span>
            <span style={{ textAlign: 'right' }}>{money(totals.cr)}</span>
            <span>
              {Math.abs(totals.dr - totals.cr) < 0.01
                ? <span style={{ color: '#16a34a' }}>Balanced</span>
                : <span style={{ color: '#dc2626' }}>Diff {money(totals.dr - totals.cr)}</span>}
            </span>
          </div>
        )}
      </div>
    </EnterpriseScreenShell>
  );
};

export default DayBookScreen;
